import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useFetch from "use-http";

import PageQuery, { getQueryStringFromPageQuery } from "common/types/PageQuery";
import * as ROUTES from "common/routes.constants";
import { PagedResponse } from "common/types/PagedResponse";
import isShortid from "utils/isShortid";
import BootGate from "contexts/BootGate";
import {
	BMS_ORG_ID,
	BMS_STAGE_ORG_ID,
	BMS_TEST_ORG_ID,
} from "views/SharedScene/StreamingStatesConstants";
import Context from "./context";
import { Project, UpdateProjectDTO } from "./types";
import useOrganizations from "../Organizations/useOrganizations";

export const initialPageQuery: PageQuery = {
	limit: 20,
	page: 1,
	search: {
		text: "",
		fields: [],
	},
	sort: "-meta.tsModified",
};

const ProjectsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { pathname, search } = useLocation();
	const history = useHistory();

	const {
		current: currentOrganization,
		loaded: currentOrganizationLoaded,
		currentOrganizationPlan,
		member,
	} = useOrganizations();

	// setCurrentProject awaits a fetch; by the time it answers, the pathname
	// captured in its closure may be stale.
	const pathnameRef = useRef(pathname);
	pathnameRef.current = pathname;

	const [query, setQuery] = useState<PageQuery>(initialPageQuery);
	const [pagedResponse, setPagedResponse] = useState<
		PagedResponse<Project>
	>();
	const append = useRef<boolean>(false);

	const [projects, setProjects] = useState<Project[]>([]);
	const [current, setCurrent] = useState<Project | undefined>();

	const [wishlists, setWishlists] = useState<any[] | null>(null);

	const { get, response, loading, error } = useFetch(
		"projects/members/getByProjects",
		{},
	);
	const { get: getCurrent, response: responseCurrent } = useFetch(
		`/projects/${current?._id}`,
	);
	const { put: putCurrent, response: responsePutCurrent } = useFetch(
		`/projects/${current?._id}`,
	);
	const { get: getByShortid, response: responseGetByShortid } = useFetch(
		`/projects`,
	);

	const fetchProjects = async () => {
		const queryString = getQueryStringFromPageQuery(query);
		await get(`?${queryString}`);
		if (response.ok) {
			const results = append.current
				? [...projects, ...response.data.results]
				: response.data.results;
			setProjects(results);
			setPagedResponse(response.data);
			append.current = false;
		}
	};

	const { get: getWishlists, response: wishlistsResponse } = useFetch(
		"wishlist/scene",
		{},
	);

	const fetchWishlists = async (sceneId : string | undefined) => {
		if (!sceneId) {
			return;
		}
		setWishlists(null);

		await getWishlists(sceneId);
		if (wishlistsResponse.ok) {
			setWishlists(wishlistsResponse.data);
		}
	};

	// Try to find current project in the projects list, or by fetching by shortid
	const setCurrentProject = async (shortid?: string) => {
		logger.debug("[ProjectsProvider] setCurrentProject", {
			shortid,
			isShortid: isShortid(shortid),
		});

		if (!isShortid(shortid)) {
			setCurrent(undefined);

			if (
				/* TODO: check if it's still needed. */
				/* (pathname.includes(ROUTES.PROJECTS) &&
					pathname !== "/admin/manage-projects") || */
				// In case user is on /expired-subscription path and change to another organization
				pathname.includes(ROUTES.EXPIRED_SUBSCRIPTION)
			) {
				// Organization has changed -> redirect
				history.push({
					pathname: `/${ROUTES.PROJECTS}`,
					search,
				});
			}

			return;
		}

		const targetProject = projects?.find((p) => p.shortid === shortid);
		if (targetProject) {
			setCurrent(targetProject);
			return;
		}

		await getByShortid(shortid);
		if (responseGetByShortid.ok) {
			setCurrent(responseGetByShortid.data);
			setProjects([...projects, responseGetByShortid.data]);
			return;
		}

		// No status means the request never got an answer (offline, aborted) —
		// that says nothing about the project, so stay put.
		if (!responseGetByShortid.status) {
			return;
		}

		// Deleted, or never visible to this user. Without this the project and
		// scene pages wait on a `current` that never comes: spinner, half-empty
		// page. Only for a /projects/:id URL that is still the one on screen —
		// other routes (/scenes/:token, …) land here with a token of their own,
		// and the user may have navigated on while the request was out.
		const [section, requested] = pathnameRef.current
			.split("/")
			.filter((chunk) => chunk.length > 1);
		if (section !== ROUTES.PROJECTS || requested !== shortid) {
			return;
		}

		setCurrent(undefined);
		// replace, not push: "back" must not return to the dead URL.
		history.replace({
			pathname: projectsGalleryIsDeadEnd()
				? `/${ROUTES.ERROR}/404`
				: `/${ROUTES.PROJECTS}`,
			search,
		});
	};

	// Where the gallery is no destination: a personal space, or a BMS
	// endcustomer (junior in the BMS org) with at most one project left. The
	// org-level half of useIsSingleProjectConsumer — the project-level signals
	// it also reads are gone along with the project.
	const projectsGalleryIsDeadEnd = () =>
		Boolean(
			currentOrganization?.isPersonal ||
				// Not isBmsOrganizationId: hooks/useIsB2cContext imports this
				// context, so importing it back would be a cycle.
				([BMS_ORG_ID, BMS_STAGE_ORG_ID, BMS_TEST_ORG_ID].includes(
					currentOrganization?._id as string,
				) &&
					member?.role === "junior" &&
					(pagedResponse?.totalResults ?? 0) <= 1),
		);

	const refreshCurrent = async () => {
		await getCurrent();
		if (responseCurrent.ok) {
			const updatedProject: Project = responseCurrent.data;
			setCurrent(updatedProject);

			const updatedProjects = projects.map((p) =>
				p._id === updatedProject?._id ? updatedProject : p,
			);
			setProjects(updatedProjects);
		}
	};

	const updateCurrent = async (updateDto: UpdateProjectDTO) => {
		await putCurrent(updateDto);

		const updatedCurrent = {
			...current,
			meta: {
				...current?.meta,
				...updateDto.meta,
			},
		} as Project;

		if (responsePutCurrent.ok) {
			setCurrent(updatedCurrent);
			const updatedProjects = projects.map((p) =>
				p._id === updatedCurrent?._id ? updatedCurrent : p,
			);
			setProjects(updatedProjects);
		}
	};

	const loadMore = () => {
		if (!pagedResponse?.hasNextPage) {
			return;
		}

		append.current = true;
		setQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));
	};

	// Handle Organization change
	useEffect(() => {
		if (currentOrganization && currentOrganizationLoaded) {
			fetchProjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganization, currentOrganizationLoaded]);

	// Handle Location change
	useEffect(() => {
		if (!currentOrganizationLoaded) {
			return;
		}

		const urlChunks = pathname.split("/");
		const [, requestedUrl] = urlChunks.filter((chunk) => chunk.length > 1);

		if (
			(!current && currentOrganizationPlan?.isExpired === false) ||
			current?.shortid !== requestedUrl
		) {
			setCurrentProject(requestedUrl);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, projects, currentOrganizationPlan]);

	useEffect(() => {
		if (currentOrganization && currentOrganizationLoaded) {
			fetchProjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query]);

	// No `loading` passed on purpose: this provider renders its children while
	// fetching, and blocking here would gate the whole app on a project list.
	return (
		<BootGate
			label="Projects"
			error={error}
			response={response}
			onRetry={fetchProjects}
		>
		<Context.Provider
			value={{
				projects,
				current,
				loading,
				error,
				query,
				pagedResponse,
				setCurrentProject,
				setQuery,
				loadMore,
				refreshCurrent,
				updateCurrent,

				wishlists,
				fetchWishlists
			}}
		>
			{children}
		</Context.Provider>
		</BootGate>
	);
};

export default ProjectsProvider;
